import { BackendClient, HttpFetch } from "@equipped/core/backend/client";

/**
 * A plain `BackendClient` for the sign-in screen (no auth headers yet — `login`/
 * `register` don't need any).
 */
export function createBackendClient(backendUrl: string | null): BackendClient | null {
  return backendUrl ? new BackendClient(backendUrl) : null;
}

/**
 * `/admin/*` accepts EITHER a session bearer token (the `Authorization: Bearer
 * <token>` header `BackendClient`'s own `authed()` sends) OR a shared
 * `X-Admin-Key` header (worker/src/index.ts `handleAdmin` — "the key lets a
 * single admin manage things without a password account"). `BackendClient` has
 * no first-class support for the latter — it only ever sends `Authorization:
 * Bearer`. Rather than fork/modify the shared, framework-free `@equipped/core`
 * client for one alternate header (which would ripple into the consumer app +
 * `apps/admin` too), we swap it at the transport layer: a custom `HttpFetch`
 * that rewrites `Authorization: Bearer <key>` into `X-Admin-Key: <key>` before
 * the request goes out. Every `BackendClient.admin*` method still "works
 * normally" — it just gets a different physical header on the wire.
 */
function adminKeyFetch(adminKey: string): HttpFetch {
  return (url, init) => {
    const headers = { ...init.headers };
    delete headers.Authorization;
    headers["X-Admin-Key"] = adminKey;
    return fetch(url, { method: init.method, headers, body: init.binaryBody ?? init.body });
  };
}

/**
 * A `BackendClient` authenticated via the shared `X-Admin-Key` instead of a
 * signed-in session. The `token` value passed to any `admin*` method is never
 * actually sent (the interceptor above always overwrites it) — callers can pass
 * the same `adminKey` string for clarity, but any value would do.
 */
export function createAdminKeyClient(backendUrl: string | null, adminKey: string): BackendClient | null {
  return backendUrl ? new BackendClient(backendUrl, adminKeyFetch(adminKey)) : null;
}
